/**
 * The scene view's presentation constants (integration plan slice 6a): the
 * town kit's material colours and finishes, texture world sizes, the shimmer
 * of reflected water, the GPU particles' shading, the reflection target, the
 * lens settings the town adds, and budgets. Lifted from lantern-water
 * (`world.js` `materials`, `artwork.js`, `water-light.js` `SHIMMER`,
 * `ambient-motion.js`, `reflection.js`, `main.js`); each group names its
 * file. Presentation only: no rule of the game reads them. Where a number is
 * the scene's own (a light, a camera framing, the grade, where things stand),
 * it is content (`scene.*` records), not here. The arithmetic inside a lifted
 * shader or procedural generator stays in its module.
 */
export const sceneLook = {
  /** `world.js` `mat()` and `materials`. */
  materials: {
    roughness: 0.94,
    facadeRoughness: 0.97,
    /** Bump scale per surface texture; the limestone's when artwork replaced the fallback, and the facade atlas's. */
    bump: {
      wood: 0.018,
      rug: 0.006,
      plaster: 0.022,
      roof: 0.035,
      slate: 0.035,
      cobble: 0.036,
      stone: 0.065,
      grass: 0.065,
      other: 0.065,
      stoneFallback: 0.065,
      stoneArtwork: 0.035,
      facade: 0.035,
    },
    /** World units one repeat of each surface covers (`artwork.js` `worldSize`; others 2). */
    worldSize: { paving: 1.65, masonry: 2.5, terracotta: 3, slate: 3, other: 2 },
    colors: {
      stoneFallback: '#cfccd0',
      stoneArtwork: '#b2afb3',
      paving: '#d3d1d0',
      roof: '#ded5c8',
      roofDark: '#c5cedc',
      wood: '#b0a183',
      darkWood: '#544d3c',
      plaster: '#eee4d4',
      limestone: '#c5c8cd',
      canopy: '#4b775f',
      peach: '#d8b99b',
      cream: '#f4ead8',
      teal: '#527d72',
      iron: '#303e3c',
      brass: '#c6a065',
      grass: '#b8b385',
      rug: '#e7e5df',
      glass: '#ecc286',
      glassEmissive: '#ffb957',
      lanternGlass: '#ffe7b4',
      windowGlass: '#fff0cb',
      windowGlassEmissive: '#ffd795',
      windowCool: '#d1e0df',
      windowCoolEmissive: '#ffc990',
      dark: '#1f302e',
      red: '#a3624e',
      canvas: '#f0d8a8',
      moss: '#6b8052',
      book1: '#3a6663',
      book2: '#aa705b',
      book3: '#a79163',
      bottle: '#6eafa3',
      bottle2: '#ad8141',
    },
    /** The paving's damp bands (x of the canal's two edges, `world.js`). */
    pavingDamp: [3.35, 11] as readonly [number, number],
    /** Contact shadows under standing boxes: opacity and height. */
    contactOpacity: 0.42,
    contactY: 0.078,
    /** Halos: a default glow's opacity, a lantern's. */
    glowOpacity: 0.46,
    lanternGlowOpacity: 0.25,
    glowColor: '#ffd899',
  },
  /** Reflected-sun shimmer on stone and timber by the canal (`water-light.js` `SHIMMER`). */
  shimmer: {
    color: [1.0, 0.86, 0.6] as readonly number[],
    strength: 2.7,
    nightStrength: 0.12,
    nightColor: [0.55, 0.7, 1.0] as readonly number[],
    wallFade: 1.3,
    undersideFade: 0.55,
    reach: 0.55,
    pixels: 32,
  },
  /** The sun's shadow camera (`main.js`: ±30 × ±28, near 0.5, far 90) and the shadow biases; the window gobos' (`window-lighting.js`). The flicker is each light's own (`flicker` in the scene record). */
  sun: { aspect: 28 / 30, near: 0.5, far: 90, bias: -0.0002, normalBias: 0.035, goboNormalBias: 0.025, goboNear: 0.1 },
  /** The canal's banks and water line where a scene has no terrain (an interior shares the town's kit shaders). */
  coast: { west: 4, east: 10.5, waterY: -0.75 },
  /** The canal's planar reflection target (`reflection.js`), in pixels. */
  reflection: { width: 768, height: 512, clipLift: 0.75 },
  /** The breeze: its strength on the Lightweight preset the game's Off borrows (`world.js` `updateEnvironment`). */
  breeze: { off: 0.45 },
  /** GPU points (`ambient-motion.js`): the pixel scale the size is computed with (`pixelScale` = drawing buffer height). */
  particles: { renderOrder: 4 },
  /** Bloom (`main.js` `UnrealBloomPass(res, 0.26, 0.6, 1.08)`): radius and threshold; the strength is the scene's. */
  bloom: { radius: 0.6, threshold: 1.08 },
  /** Contact occlusion (`engine/src/occlusion.js`): GTAO samples and radius, thickness, distance falloff, strength. */
  occlusion: { samples: 12, radius: 0.85, thickness: 0.55, distanceExponent: 1.6, intensity: 0.72 },
  /** The town lens (`engine/src/focus.js` defaults): taps, tap radius in pixels, focus band, lens strength, grain. */
  lens: { taps: 16, tapPixels: 5.5, focusNear: 3.8, focusFar: 17, strength: 1.1, focusHeight: 0.8, waterBand: [-0.66, -0.55] as readonly [number, number], shadowLift: [0.006, 0.0025, 0.014] as readonly [number, number, number], shadowChroma: 0.006, hazeRange: [50, 125] as readonly [number, number], zoneMoodRate: 1.6 },
  /** The airship's shafts below deck (`airship-air.js`): a cool shaft colour and a faint warm pool (ADR 0033). */
  shipAir: { shaftColor: [0.22, 0.28, 0.35] as readonly [number, number, number], poolTint: [0.005, 0.004, 0.002, 1.28] as readonly [number, number, number, number] },
  /** Pixel ratio caps: Medium at most 1.5 (the Rendering page), Off 1. */
  pixelRatio: { medium: 1.5, off: 1 },
  /** The traveler's projected cut-out shadow (`engine/src/character-shadows.js`): opacity by day, march steps, refine steps, split height. */
  characterShadow: { opacity: 0.25, march: 12, refine: 6, split: 0.32, lift: 0.012, segments: [8, 12] as readonly [number, number] },
  /** The selective silhouette (`visibility.js`): rim and fill colours, the depth margin. */
  hint: { edge: '#eed397', fill: '#9bb6b3', margin: 0.08 },
  /** The blob shadow under a character (`world.js` `character`): at Off under everyone, at Medium under a proxy (a sprite casts its projected cut-out). */
  blob: { color: '#172322', opacity: 0.32 },
  /** A sprite's self-light at Medium (`main.js`: the illustrated traveler's emissive 0.8 keeps it legible on the warm cobbles). */
  emissive: { traveler: 0.8 },
  /** A walk or run cycle's length in metres walked (`main.js`: `heroCycle += traveled / (running ? 2.5 : 1.8)`), so the feet do not slide. */
  stride: { walk: 1.8, run: 2.5 },
  /**
   * The flocks (`engine/src/birds.js`): the scatter's seed, the cards' self-light by day and its fall at night,
   * how near (m beyond its radius) an actor keeps a flock away, the seconds of quiet before it lands, after which
   * a flock in the air counts as away, and a landing as settled, a walking resident's own scare radius
   * (`main.js`: only when it passes right by), and the flee direction's bias by takeoff.
   */
  birds: {
    seed: 7717,
    emissive: 0.42,
    emissiveNight: 0.24,
    linger: 1.5,
    quiet: 7,
    away: 5,
    settle: 6,
    residentRadius: 0.9,
    fleeBias: [
      [0, -0.6],
      [0.8, -0.2],
      [-0.8, -0.2],
      [0, 0.55],
    ] as readonly (readonly [number, number])[],
  },
  /** `pickGround`: the march along the camera ray (m a step) and the bisection steps that refine the crossing. */
  pick: { step: 0.25, bisect: 12 },
  /** Below this canvas width (CSS px) the camera target tracks x more closely (`main.js`: `innerWidth < 600`). */
  narrowWidth: 600,
  /** An M sprite cell (the sprite contract: 48 × 64 at 29 px per metre): `spriteScale` is metres per this plane's height. */
  mCell: { height: 64, pxPerMetre: 29 },
  /** The proxies' colours where no theme is given: the traveler's coat, the residents', their heads, the blob shadow. */
  proxy: { party: '#406e87', resident: '#897351', head: '#e8d2b0', trim: '#3a3a34', background: '#172322' },
  /** The lantern glass's brightness from day to night (`main.js`). */
  lanternGlass: [0.7, 1.25] as readonly [number, number],
  /**
   * The airship's mooring in the boat landing's opening in the coping (ADR 0032; not in the prototype), metres, in
   * the prefab's own axes (three.js: x east, y up, z south; the origin is the gangway's foot on the quay, the
   * centre of its width). `quayTop`: the paving's top there. The iron bollard (where it stands, its post with the
   * taper toward its top, its cap, and the neck the rope is tied round, a fraction of its height). The gangway
   * (its length east over the water, its width between the rails, its rise in degrees, the timber sill its foot
   * rests on (clearing the coping's 0.10), the planks (size, the share of a plank's pitch that is timber, their
   * thickness), the side stringers under them, the rails (height, posts per side, the post radius, the inset of
   * the end posts, the rail's thickness)). The davit that holds the gangway's far end and carries the mooring
   * ring (where its mast stands, its radius, taper and height, the arm's reach east and droop, the brace from the mast
   * to the arm (heights as fractions), the ring, and the bridle down to the gangway's end posts). The rope (its
   * radius and its sag between the bollard and the ring). The sign (its post, the arm it hangs from (reaching
   * west), the board's size, its drop under the arm, the cream face's border, and the airship glyph on it: hull,
   * gondola, fin and struts, in metres on the face; no lettering).
   */
  airshipMooring: {
    quayTop: 0.02,
    bollard: { x: -0.25, z: -0.35, radius: 0.13, height: 0.55, taper: 0.85, capRadius: 0.17, capHeight: 0.08, neck: 0.8 },
    gangway: {
      length: 2.1,
      width: 0.52,
      rise: 10,
      sill: { height: 0.12, depth: 0.3 },
      plank: 0.21,
      timber: 0.9,
      thickness: 0.06,
      stringer: 0.07,
      rail: { height: 0.85, posts: 4, post: 0.035, inset: 0.05, thickness: 0.03 },
    },
    davit: {
      x: 0,
      z: -0.5,
      radius: 0.06,
      taper: 0.8,
      height: 2.55,
      reach: 1.95,
      droop: 0.1,
      arm: 0.05,
      brace: { from: 0.66, to: 0.45, thickness: 0.035 },
      ring: { radius: 0.11, tube: 0.022 },
      bridle: 0.014,
    },
    rope: { radius: 0.024, sag: 0.18 },
    sign: {
      x: -0.5,
      z: -0.65,
      radius: 0.045,
      height: 2.05,
      arm: { reach: 0.68, below: 0.1, thickness: 0.035 },
      board: { at: 0.4, width: 0.56, height: 0.38, depth: 0.04, drop: 0.09, border: 0.035, chain: 0.012, chainSpread: 0.19 },
      glyph: {
        hull: { y: 0.035, rx: 0.15, ry: 0.055 },
        gondola: { y: -0.075, width: 0.08, height: 0.03 },
        fin: { x: -0.155, y: 0.035, width: 0.04, height: 0.085 },
        strut: { spread: 0.03, width: 0.01, from: 0.8 },
        relief: 0.006,
      },
    },
  },
  /** Off renders on change only, and at most this often while a sprite's loop plays on screen. */
  offIdleFps: 12,
  /** How far (m) the camera's target may still be from its goal and count as settled (Off stops drawing then). */
  settle: 0.002,
} as const;
